import { supabase } from '../lib/supabase';

export interface PendingDataLogInput {
  stationObjectId?: number;
  propertyAddress?: string;
  locationData?: {
    latitude?: number;
    longitude?: number;
    city?: string;
    state?: string;
    zipCode?: string;
  };
  errorMessage: string;
}

/**
 * Logs a property that has insufficient data for TAM projections
 * This helps track which properties need more data collection
 */
export async function logPendingDataProperty(input: PendingDataLogInput): Promise<{ success: boolean; error?: string }> {
  try {
    // Get current user if authenticated
    const { data: { user } } = await supabase.client.auth.getUser();

    const { error } = await supabase.client
      .from('properties_pending_data')
      .insert({
        station_object_id: input.stationObjectId || null,
        property_address: input.propertyAddress || null,
        location_data: input.locationData || null,
        error_message: input.errorMessage,
        user_id: user?.id || null,
        resolved: false
      });

    if (error) {
      console.error('Error logging pending data property:', error);
      return { success: false, error: error.message };
    }

    console.log('[PendingDataService] Successfully logged property needing data:', {
      stationObjectId: input.stationObjectId,
      address: input.propertyAddress
    });

    return { success: true };
  } catch (err: any) {
    console.error('Exception logging pending data property:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Checks if a property has already been logged as needing data
 * This prevents duplicate entries
 */
export async function checkIfPropertyAlreadyLogged(
  stationObjectId?: number,
  propertyAddress?: string
): Promise<boolean> {
  try {
    let query = supabase.client
      .from('properties_pending_data')
      .select('id')
      .eq('resolved', false);

    if (stationObjectId) {
      query = query.eq('station_object_id', stationObjectId);
    } else if (propertyAddress) {
      query = query.eq('property_address', propertyAddress);
    } else {
      return false; // Can't check without identifier
    }

    const { data, error } = await query.limit(1).single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
      console.error('Error checking if property already logged:', error);
      return false;
    }

    return !!data;
  } catch (err) {
    console.error('Exception checking if property already logged:', err);
    return false;
  }
}

/**
 * Gets all pending data properties for the current user
 */
export async function getUserPendingDataProperties() {
  try {
    const { data: { user } } = await supabase.client.auth.getUser();

    if (!user) {
      return { data: null, error: 'Not authenticated' };
    }

    const { data, error } = await supabase.client
      .from('properties_pending_data')
      .select('*')
      .eq('user_id', user.id)
      .order('requested_at', { ascending: false });

    if (error) {
      console.error('Error fetching user pending data properties:', error);
      return { data: null, error: error.message };
    }

    return { data, error: null };
  } catch (err: any) {
    console.error('Exception fetching user pending data properties:', err);
    return { data: null, error: err.message };
  }
}
